import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import {
  Box,
  Container,
  Text,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  Button,
  VStack,
  useToast,
  Heading,
  InputGroup,
  InputLeftElement,
} from "@chakra-ui/react";
import { AiOutlinePhone, AiOutlineMail } from "react-icons/ai";
import { useSearchParams } from "react-router-dom";
import axios from "axios";

const ContactUsPage = () => {
  const [searchParams] = useSearchParams();
  const [issueType, setIssueType] = useState("");
  const [description, setDescription] = useState("");
  const [contactMethod, setContactMethod] = useState("");
  const [contactValue, setContactValue] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  const toast = useToast();

  // Pre-fill issue type from URL parameter
  useEffect(() => {
    const typeParam = searchParams.get("type");
    if (typeParam) {
      setIssueType(typeParam);
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (
      !issueType ||
      !description ||
      !contactMethod ||
      !contactValue ||
      !name
    ) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields",
        status: "warning",
        duration: 5000,
        isClosable: true,
        position: "bottom",
      });
      setLoading(false);
      return;
    }

    // Validate contact value based on method
    if (contactMethod === "email") {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(contactValue)) {
        toast({
          title: "Invalid Email",
          description: "Please enter a valid email address",
          status: "warning",
          duration: 5000,
          isClosable: true,
          position: "bottom",
        });
        setLoading(false);
        return;
      }
    } else if (contactMethod === "phone") {
      const phoneRegex = /^[\d\s\-\+\(\)]+$/;
      if (!phoneRegex.test(contactValue) || contactValue.length < 10) {
        toast({
          title: "Invalid Phone Number",
          description: "Please enter a valid phone number",
          status: "warning",
          duration: 5000,
          isClosable: true,
          position: "bottom",
        });
        setLoading(false);
        return;
      }
    }

    try {
      const config = {
        headers: {
          "Content-type": "application/json",
        },
      };

      await axios.post(
        "/api/support/contact",
        {
          name,
          issueType,
          description,
          contactMethod,
          contactValue,
        },
        config
      );

      toast({
        title: "Message Sent!",
        description:
          "We've received your message and will get back to you soon.",
        status: "success",
        duration: 5000,
        isClosable: true,
        position: "bottom",
      });

      // Reset form
      setName("");
      setIssueType("");
      setDescription("");
      setContactMethod("");
      setContactValue("");
      setLoading(false);
    } catch (error) {
      const message = error.response
        ? error.response.data.message
        : error.message;
      toast({
        title: "Error Occurred!",
        description: message,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "bottom",
      });
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>ATO | Contact Us</title>
        <meta
          name="description"
          content="Contact ATO for support, questions, or feedback. Fill out our contact form and our team will respond as soon as possible."
        />
      </Helmet>
      <Box bg="gray.50" minH="100vh">
        {/* Hero Section */}
        <Box
          bg="white"
          pt={{ base: "60px", md: "100px" }}
          pb={{ base: "40px", md: "60px" }}
          px={{ base: 4, md: 8 }}
          textAlign="center"
          borderBottom="1px solid"
          borderColor="gray.200"
        >
          <Heading
            fontSize={{ base: "4xl", md: "6xl" }}
            fontWeight="800"
            color="gray.900"
            fontFamily="'Work Sans', sans-serif"
            mb={4}
          >
            Contact Us
          </Heading>
          <Text
            fontSize={{ base: "lg", md: "xl" }}
            color="gray.600"
            maxW="700px"
            mx="auto"
          >
            Have an issue or question? We're here to help. Fill out the form
            below and we'll get back to you as soon as possible.
          </Text>
        </Box>

        {/* Contact Form */}
        <Container maxW="800px" py={{ base: 8, md: 12 }}>
          <Box
            bg="white"
            borderRadius="xl"
            border="1px solid"
            borderColor="gray.200"
            p={{ base: 6, md: 8 }}
            boxShadow="md"
          >
            <form onSubmit={handleSubmit}>
              <VStack spacing={6} align="stretch">
                {/* Name Field */}
                <FormControl isRequired>
                  <FormLabel color="gray.700" fontWeight="600">
                    Your Name
                  </FormLabel>
                  <Input
                    placeholder="Enter your full name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    size="lg"
                    focusBorderColor="orange.400"
                  />
                </FormControl>

                {/* Issue Type Dropdown */}
                <FormControl isRequired>
                  <FormLabel color="gray.700" fontWeight="600">
                    Issue Type
                  </FormLabel>
                  <Select
                    placeholder="Select issue type"
                    value={issueType}
                    onChange={(e) => setIssueType(e.target.value)}
                    size="lg"
                    focusBorderColor="orange.400"
                  >
                    <option value="unsatisfactory-work">
                      Unsatisfactory Work
                    </option>
                    <option value="payment-issues">Payment Issues</option>
                    <option value="vendor-dispute">Vendor Dispute</option>
                    <option value="technical-problem">Technical Problem</option>
                    <option value="account-issue">Account Issue</option>
                    <option value="other">Other</option>
                  </Select>
                </FormControl>

                {/* Description Field */}
                <FormControl isRequired>
                  <FormLabel color="gray.700" fontWeight="600">
                    Describe Your Issue
                  </FormLabel>
                  <Textarea
                    placeholder="Please provide as much detail as possible about your issue..."
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    size="lg"
                    minHeight="150px"
                    focusBorderColor="orange.400"
                  />
                </FormControl>

                {/* Contact Method */}
                <FormControl isRequired>
                  <FormLabel color="gray.700" fontWeight="600">
                    Preferred Contact Method
                  </FormLabel>
                  <Select
                    placeholder="Select how we should contact you"
                    value={contactMethod}
                    onChange={(e) => {
                      setContactMethod(e.target.value);
                      setContactValue("");
                    }}
                    size="lg"
                    focusBorderColor="orange.400"
                  >
                    <option value="email">Email</option>
                    <option value="phone">Phone Number</option>
                  </Select>
                </FormControl>

                {/* Contact Value Field */}
                {contactMethod && (
                  <FormControl isRequired>
                    <FormLabel color="gray.700" fontWeight="600">
                      {contactMethod === "email"
                        ? "Email Address"
                        : "Phone Number"}
                    </FormLabel>
                    <InputGroup size="lg">
                      <InputLeftElement pointerEvents="none">
                        {contactMethod === "email" ? (
                          <AiOutlineMail color="gray" />
                        ) : (
                          <AiOutlinePhone color="gray" />
                        )}
                      </InputLeftElement>
                      <Input
                        type={contactMethod === "email" ? "email" : "tel"}
                        placeholder={
                          contactMethod === "email"
                            ? "your-email@example.com"
                            : "+1 (555) 123-4567"
                        }
                        value={contactValue}
                        onChange={(e) => setContactValue(e.target.value)}
                        focusBorderColor="orange.400"
                        pl="2.5rem"
                      />
                    </InputGroup>
                  </FormControl>
                )}

                {/* Submit Button */}
                <Button
                  type="submit"
                  colorScheme="orange"
                  size="lg"
                  width="100%"
                  isLoading={loading}
                  loadingText="Sending..."
                  mt={4}
                >
                  Submit Request
                </Button>
              </VStack>
            </form>
          </Box>

          {/* Additional Info */}
          <Box mt={8} textAlign="center">
            <Text color="gray.600" fontSize="sm">
              We typically respond within 24-48 hours during business days.
            </Text>
            <Text color="gray.600" fontSize="sm" mt={2}>
              For urgent matters, please call our support hotline.
            </Text>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default ContactUsPage;
